import React, { useEffect, useState, useCallback, useRef } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  LogOut,
  Upload,
  CheckCircle2,
  Clock,
  Send,
  FileText,
  RefreshCw,
  Phone,
  FileSignature,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import CFLogo from "@/components/CFLogo";
import { AUTH } from "@/constants/testIds";
import { APPLY_LINK_PROPS } from "@/constants/links";

const STATUS_FLOW = [
  { key: "pending", label: "Pending review" },
  { key: "under_review", label: "Under review" },
  { key: "documents_needed", label: "Documents needed" },
  { key: "approved", label: "Approved" },
  { key: "funded", label: "Funded" },
];

const STATUS_LABELS = {
  pending: "Pending review",
  under_review: "Under review",
  documents_needed: "Documents needed",
  approved: "Approved",
  funded: "Funded",
  declined: "Declined",
};

const STATUS_TONES = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  under_review: "bg-sky-50 text-sky-800 border-sky-200",
  documents_needed: "bg-rose-50 text-rose-800 border-rose-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  funded: "bg-cf-mint text-cf-ink border-cf-gold/30",
  declined: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

const fmt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const MerchantDashboard = () => {
  const { user, logout, api } = useAuth();
  const [lead, setLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loadingLead, setLoadingLead] = useState(true);
  const [error, setError] = useState("");
  const fileInputs = useRef({});

  const loadAll = useCallback(async () => {
    setLoadingLead(true);
    setError("");
    try {
      const { data } = await api.get("/merchant/lead");
      setLead(data);
      const mRes = await api.get(`/leads/${data.id}/messages`);
      setMessages(mRes.data);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || "Could not load your application.");
    } finally {
      setLoadingLead(false);
    }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (user === null) {
    return <div className="min-h-screen bg-cf-cream text-cf-slate flex items-center justify-center">Loading…</div>;
  }
  if (user === false) return <Navigate to="/merchant/login" replace />;
  if (user.role !== "merchant") return <Navigate to="/agent/dashboard" replace />;

  // ----- handlers -----
  const upload = async (docId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("lead_id", lead.id);
    try {
      await axios.post(`${api.defaults.baseURL}/merchant/documents/${docId}/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadAll();
    } catch (err) {
      alert(err?.response?.data?.detail || "Upload failed");
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !lead) return;
    setDraft("");
    try {
      const { data } = await api.post(`/leads/${lead.id}/messages`, { body });
      setMessages((prev) => [...prev, data]);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not send message");
    }
  };

  // ----- render -----
  const currentStep = STATUS_FLOW.findIndex((s) => s.key === (lead?.status || "pending"));
  const isDeclined = lead?.status === "declined";

  return (
    <div data-testid={AUTH.merchantDashboard} className="min-h-screen bg-cf-cream text-cf-ink">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-cf-cream/85 border-b border-cf-line/70">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <CFLogo testId="merchant-header-logo" />
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs text-cf-slate">
              {user.email}
            </span>
            <button onClick={loadAll} className="cf-btn-ghost-light text-xs py-2 px-4">
              <RefreshCw size={13} strokeWidth={1.8} /> Refresh
            </button>
            <button
              data-testid={AUTH.merchantLogout}
              onClick={logout}
              className="cf-btn-ghost-light text-xs py-2 px-4"
            >
              <LogOut size={13} strokeWidth={1.8} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {loadingLead && <div className="text-cf-slate text-sm">Loading your application…</div>}
        {error && !loadingLead && (
          <div className="rounded-2xl bg-white border border-cf-line p-8 text-center">
            <p className="text-cf-ink">{error}</p>
            <a {...APPLY_LINK_PROPS} data-testid="merchant-dashboard-apply-link" className="cf-btn-gold mt-6">Apply for funding</a>
          </div>
        )}

        {lead && !error && (
          <>
            {/* Greeting */}
            <div className="mb-8">
              <span className="cf-overline">Your application</span>
              <h1 className="font-serif text-3xl md:text-4xl tracking-tight mt-2">
                Hi {user.name.split(" ")[0]} — here&apos;s your <span className="italic text-cf-gold">progress</span>.
              </h1>
              <p className="text-cf-ink-soft text-sm mt-2">
                Submitted {fmt(lead.created_at)} · {lead.business_name}
              </p>
            </div>

            {/* Onboarding banner — visible until bank statements are uploaded */}
            {(() => {
              const reqs = lead.required_documents || [];
              const initialPending = reqs.filter(
                (d) => /bank statement|month-to-date/i.test(d.name) && d.status !== "submitted"
              );
              if (initialPending.length === 0) return null;
              return (
                <div data-testid="onboarding-banner" className="rounded-2xl bg-white border border-cf-gold/40 p-6 md:p-8 mb-8 shadow-sm">
                  <div className="cf-overline text-cf-gold-dark">Next step</div>
                  <h2 className="font-serif text-2xl md:text-3xl mt-2 text-cf-ink">
                    Upload your last <span className="italic text-cf-gold">{initialPending.length}</span>{" "}
                    {initialPending.length === 1 ? "statement" : "statements"} to start your review.
                  </h2>
                  <p className="text-cf-ink-soft text-sm mt-3 leading-relaxed">
                    We just need your business bank statements (PDFs straight from online banking work great).
                    {initialPending.some((d) => /month-to-date/i.test(d.name)) && " Since it's past the 15th, please also include the current month-to-date statement."}
                    Once all are in, our team will review and respond within 1 business day.
                  </p>
                </div>
              );
            })()}

            {/* Post-approval banner — once approved, prompt for DL + voided check */}
            {lead.status === "approved" && (() => {
              const reqs = lead.required_documents || [];
              const postApprovalPending = reqs.filter(
                (d) => /driver's license|voided check/i.test(d.name) && d.status !== "submitted"
              );
              if (postApprovalPending.length === 0) return null;
              return (
                <div data-testid="post-approval-banner" className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 md:p-8 mb-8">
                  <div className="cf-overline text-emerald-700">You&apos;re approved 🎉</div>
                  <h2 className="font-serif text-2xl md:text-3xl mt-2 text-cf-ink">
                    One last step — upload your <span className="italic text-cf-gold">driver&apos;s license</span> and <span className="italic text-cf-gold">voided check</span>.
                  </h2>
                  <p className="text-cf-ink-soft text-sm mt-3 leading-relaxed">
                    Color copy of your driver&apos;s license + a voided business check so we know where to wire funds. As soon as those land we&apos;ll send your contract.
                  </p>
                </div>
              );
            })()}

            {/* Status banner */}
            <div className={`rounded-2xl border p-6 md:p-8 mb-8 ${STATUS_TONES[lead.status] || STATUS_TONES.pending}`} data-testid={AUTH.merchantStatusBadge}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold opacity-70">Current status</div>
                  <div className="font-serif text-2xl md:text-3xl mt-1">{STATUS_LABELS[lead.status] || lead.status}</div>
                </div>
                <div className="text-sm">
                  {lead.status === "pending" && "We've received your application — an advisor will reach out shortly."}
                  {lead.status === "under_review" && "Your file is in active review with our credit team."}
                  {lead.status === "documents_needed" && "Please upload the requested documents below."}
                  {lead.status === "approved" && "Congratulations — you've been approved. Watch for next-step instructions."}
                  {lead.status === "funded" && "Funds have been wired. Welcome to Capless!"}
                  {lead.status === "declined" && "Unfortunately we couldn't approve this file. Reach out and we'll see if a different product works."}
                </div>
              </div>

              {/* Progress timeline */}
              {!isDeclined && (
                <div className="mt-8 flex items-center gap-2 overflow-x-auto">
                  {STATUS_FLOW.map((s, i) => {
                    const reached = i <= currentStep;
                    return (
                      <React.Fragment key={s.key}>
                        <div className="flex flex-col items-center gap-2 shrink-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border ${reached ? "bg-cf-ink text-white border-cf-ink" : "bg-white text-cf-slate border-cf-line"}`}>
                            {reached ? <CheckCircle2 size={14} strokeWidth={2} /> : i + 1}
                          </div>
                          <div className={`text-[10px] uppercase tracking-[0.15em] whitespace-nowrap ${reached ? "text-cf-ink font-semibold" : "text-cf-slate"}`}>{s.label}</div>
                        </div>
                        {i < STATUS_FLOW.length - 1 && (
                          <div className={`h-px flex-1 min-w-6 ${i < currentStep ? "bg-cf-ink" : "bg-cf-line"}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Application summary */}
              <section className="bg-white rounded-2xl border border-cf-line p-6 md:p-8">
                <div className="cf-overline mb-4">Application summary</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                  <Field label="Business" value={lead.business_name} />
                  <Field label="Program" value={lead.product} />
                  <Field label="Amount" value={lead.funding_amount} />
                  <Field label="Phone" value={lead.phone} />
                  <Field label="Monthly Rev." value={lead.monthly_revenue || "—"} />
                  <Field label="Time in Biz" value={lead.time_in_business || "—"} />
                </div>
                {lead.notes && (
                  <div className="mt-6 pt-5 border-t border-cf-line">
                    <div className="cf-overline mb-2">Notes you sent</div>
                    <p className="text-cf-ink-soft text-sm leading-relaxed">{lead.notes}</p>
                  </div>
                )}
                <div className="mt-6 pt-5 border-t border-cf-line flex items-center gap-2 text-sm text-cf-ink-soft">
                  <Phone size={14} className="text-cf-gold" />
                  Need to talk now? Call <a href="tel:+17377757077" className="text-cf-ink font-semibold cf-link-underline">(737) 775-7077</a>
                </div>
              </section>

              {/* Required documents */}
              <section
                data-testid={AUTH.merchantDocsList}
                className="bg-white rounded-2xl border border-cf-line p-6 md:p-8"
              >
                <div className="cf-overline mb-4">Required documents</div>
                {(!lead.required_documents || lead.required_documents.length === 0) ? (
                  <div className="text-cf-ink-soft text-sm py-4">
                    No documents are requested yet. If your file moves to <em>Documents needed</em>, your advisor will list what they need here.
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {lead.required_documents.map((d) => (
                      <li key={d.id} className="flex items-center gap-3 p-3 rounded-xl border border-cf-line hover:border-cf-gold/40 transition">
                        <div className="w-9 h-9 rounded-lg bg-cf-mint flex items-center justify-center shrink-0">
                          <FileText size={15} strokeWidth={1.8} className="text-cf-ink" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-cf-ink font-medium truncate">{d.name}</div>
                          {d.status === "submitted" ? (
                            <div className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1">
                              <CheckCircle2 size={12} /> {d.file_name} · uploaded {fmt(d.submitted_at)}
                            </div>
                          ) : (
                            <div className="text-xs text-cf-slate mt-0.5 flex items-center gap-1">
                              <Clock size={12} /> Awaiting upload
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          ref={(el) => { fileInputs.current[d.id] = el; }}
                          onChange={(e) => upload(d.id, e.target.files?.[0])}
                        />
                        <button
                          onClick={() => fileInputs.current[d.id]?.click()}
                          className={`text-xs font-semibold px-3 py-2 rounded-full inline-flex items-center gap-1.5 transition ${
                            d.status === "submitted"
                              ? "bg-cf-line text-cf-ink hover:bg-cf-mint"
                              : "bg-cf-ink text-white hover:bg-cf-ink-soft"
                          }`}
                        >
                          <Upload size={12} strokeWidth={2} />
                          {d.status === "submitted" ? "Replace" : "Upload"}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-cf-slate mt-5 leading-relaxed">
                  Max file size 10 MB. PDF, JPG, PNG and most common document types accepted.
                </p>
              </section>
            </div>

            {/* Contract status — shown once approved */}
            {lead.status === "approved" && (
              <section data-testid="merchant-contract-status" className="bg-white rounded-2xl border border-cf-line p-6 md:p-8 mt-6">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="cf-overline flex items-center gap-1.5">
                    <FileSignature size={12} className="text-cf-gold" /> Contract
                  </div>
                </div>
                {(() => {
                  const cs = lead.contract_status || "not_sent";
                  const map = {
                    not_sent: {
                      title: "Not yet sent",
                      sub: "Once your driver's license and voided check are in, we'll send your contract to e-sign.",
                      tone: "text-cf-ink-soft",
                    },
                    out_for_signature: {
                      title: "Contract is out for signature ✍️",
                      sub: "Check your email for the e-sign link. Reply here if you can't find it.",
                      tone: "text-cf-gold-dark",
                    },
                    signed: {
                      title: "Signed — funding in progress 🎉",
                      sub: "We've received your signed contract. Funds typically wire within 1 business day.",
                      tone: "text-emerald-700",
                    },
                  };
                  const m = map[cs];
                  return (
                    <>
                      <div className={`font-serif text-2xl mt-1 ${m.tone}`}>{m.title}</div>
                      <p className="text-cf-ink-soft text-sm mt-2 leading-relaxed">{m.sub}</p>
                    </>
                  );
                })()}
              </section>
            )}

            {/* Chat */}
            <section className="bg-white rounded-2xl border border-cf-line p-6 md:p-8 mt-6" data-testid={AUTH.merchantMessages}>
              <div className="flex items-baseline justify-between mb-5">
                <div>
                  <div className="cf-overline">Messages with your advisor</div>
                  <p className="text-cf-slate text-xs mt-1">Replies during business hours: Mon–Fri, 9 a.m.–7 p.m. ET.</p>
                </div>
              </div>

              <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1 mb-5">
                {messages.length === 0 && (
                  <div className="text-cf-ink-soft text-sm py-6 text-center">
                    No messages yet. Send the first one — we usually reply within a business hour.
                  </div>
                )}
                {messages.map((m) => {
                  const mine = m.from_role === "merchant";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] rounded-2xl px-4 py-3 ${mine ? "bg-cf-ink text-white" : "bg-cf-mint text-cf-ink"}`}>
                        <div className={`text-[0.65rem] uppercase tracking-[0.16em] mb-1 ${mine ? "text-white/70" : "text-cf-ink-soft"}`}>
                          {mine ? "You" : `${m.from_name} · Capless`} · {fmt(m.created_at)}
                        </div>
                        <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={sendMessage} className="flex items-center gap-3">
                <input
                  data-testid={AUTH.merchantMessageInput}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="cf-input-light flex-1"
                  placeholder="Type a message to your advisor…"
                />
                <button
                  data-testid={AUTH.merchantMessageSend}
                  type="submit"
                  disabled={!draft.trim()}
                  className="cf-btn-primary disabled:opacity-40"
                >
                  <Send size={14} strokeWidth={2} /> Send
                </button>
              </form>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-slate mb-1">{label}</div>
    <div className="text-cf-ink break-words">{value}</div>
  </div>
);

export default MerchantDashboard;
